import * as React from "react";
import Drawer from "@mui/material/Drawer";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userDashboardSelector } from "../../../redux/userDashboard";
import { useTranslation } from "react-i18next";
import SidebarNav from "./SidebarNav";
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from "./sidebarWidth";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { closeSession } from "../../../lib/session";
import { shouldFetch } from "../../../lib/freshData";
import { GetAgentNotificationsDataService, GetNotificationsDataService } from "../../../services/CommonService";

// The layout that offsets the content reads the same constant, so the two
// can no longer disagree.
const railWidth = (collapsed) =>
  collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

export default function Sidebar({
  isAgentPanel,
  mobileOpen,
  handleDrawerToggle,
  hideSidebarOnDesktop,
  collapsed = false,
  onCollapse,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // The bell used to live in the top bar. Its count comes from the same
  // store either way.
  const { notificationsDataListing, agentNotificationsDataListing } =
    useSelector(userDashboardSelector);
  /* The count on the bell used to appear only after opening Notifications,
     because that page was the only thing that ever fetched the list. The
     sidebar asks for it itself, once a minute at most. */
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (shouldFetch(`notifications:${isAgentPanel ? "agent" : "customer"}`, 60_000)) {
      dispatch(isAgentPanel ? GetAgentNotificationsDataService() : GetNotificationsDataService());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentPanel]);

  const notificationCount =
    (isAgentPanel
      ? agentNotificationsDataListing?.items?.length
      : notificationsDataListing?.items?.length) || 0;

  const navigate = useNavigate();
  const handleLogout = () => {
    // Only the session goes: clear() was also taking the language and the
    // draft of whatever the person was in the middle of.
    closeSession(isAgentPanel ? "agent" : "customer");
    navigate("/");
  };

  const UserGroups = [
    {
      /* This used to be "Home", which left the panel for the public
         catalogue — the one menu entry that took you out of the place you
         had just signed in to. Browsing is a button on the dashboard now,
         where it is an action rather than a section. */
      items: [
        {
          name: t("Home.Title", { defaultValue: "Dashboard" }),
          to: "/user-dashboard",
          icon: <HomeRoundedIcon fontSize="small" />,
        },
      ],
    },
    {
      label: t("Headings.SidebarMine", { defaultValue: "Mine" }),
      items: [
        {
          name: t("Headings.My Property"),
          to: "/my-property",
          icon: <ApartmentRoundedIcon fontSize="small" />,
        },
        {
          name: t("Headings.My Visits"),
          to: "/user-schedule-visit",
          icon: <EventAvailableRoundedIcon fontSize="small" />,
        },
        {
          name: t("Headings.My Wishlist"),
          to: "/wishlists",
          icon: <FavoriteBorderRoundedIcon fontSize="small" />,
        },
        {
          name: t("Headings.MyDeals", { defaultValue: "My deals" }),
          to: "/my-deals",
          icon: <HandshakeRoundedIcon fontSize="small" />,
        },
      ],
    },
    {
      label: t("Headings.SidebarAccount", { defaultValue: "Account" }),
      items: [
        {
          name: t("Headings.Notifications", { defaultValue: "Notifications" }),
          to: "/user-settings?tab=notifications",
          icon: <NotificationsNoneRoundedIcon fontSize="small" />,
          badge: notificationCount,
        },
        {
          name: t("Settings.Settings"),
          to: "/user-settings?tab=4",
          icon: <SettingsOutlinedIcon fontSize="small" />,
        },
      ],
    },
  ];

  const AgentGroups = [
    {
      items: [
        {
          name: t("Agent_dashboard.Dashboard"),
          to: "/agent-dashboard",
          icon: <HomeRoundedIcon fontSize="small" />,
        },
      ],
    },
    {
      /* Merging pages left three headings over one entry each, and the gap
         meant for sections fell between them. The agent's work is one
         section; notifications and settings are the account, as on the
         customer's side. */
      label: t("Headings.SidebarWork", { defaultValue: "Work" }),
      items: [
        {
          name: t("Schedule.Title", { defaultValue: "Schedule" }),
          to: "/agent-schedule",
          icon: <EventAvailableRoundedIcon fontSize="small" />,
        },
        {
          name: t("Commissions.Title", { defaultValue: "Commissions" }),
          to: "/my-commissions",
          icon: <PaidOutlinedIcon fontSize="small" />,
        },
      ],
    },
    {
      label: t("Headings.SidebarTeam", { defaultValue: "Team" }),
      items: [
        {
          /* Inviting an agent happens from the team page, where the team
             is — the same place the button already was. */
          name: t("Team.MyAgents", { defaultValue: "My agents" }),
          to: "/my-agents-list",
          icon: <GroupsOutlinedIcon fontSize="small" />,
        },
      ],
    },
    {
      label: t("Headings.SidebarAccount", { defaultValue: "Account" }),
      items: [
        {
          name: t("Headings.Notifications", { defaultValue: "Notifications" }),
          to: "/agent-settings?tab=notifications",
          icon: <NotificationsNoneRoundedIcon fontSize="small" />,
          badge: notificationCount,
        },
        {
          name: t("Settings.Settings"),
          to: "/agent-settings?tab=4",
          icon: <SettingsOutlinedIcon fontSize="small" />,
        },
      ],
    },
  ];

  const groups = isAgentPanel ? AgentGroups : UserGroups;

  // Who is signed in, for the block at the foot of the sidebar.
  const signedIn = (() => {
    try {
      const raw = localStorage.getItem(isAgentPanel ? "agent_user" : "user");
      const info = raw && JSON.parse(raw);
      if (!info) return null;
      const name = [info.first_name, info.last_name].filter(Boolean).join(" ");
      if (!name) return null;
      return {
        name,
        role: isAgentPanel
          ? t("Headings.RoleAgent", { defaultValue: "Agent" })
          : t("Headings.RoleCustomer", { defaultValue: "Customer" }),
        initials: name
          .split(" ")
          .map((word) => word[0]?.toUpperCase())
          .slice(0, 2)
          .join(""),
      };
    } catch (e) {
      return null;
    }
  })();

  const isRouteActive = (routePath) => {
    const [path, search] = routePath.split("?");
    const normalizedSearch = search ? `?${search}` : "";

    return location.pathname === path && (normalizedSearch === "" || location.search === normalizedSearch);
  };

  return (
    <>
      {/* Desktop */}
      {!hideSidebarOnDesktop && (
        <Drawer
          sx={{
            display: { xs: "none", md: "block" },
            width: railWidth(collapsed),
            flexShrink: 0,
            transition: "width .2s ease",
            "& .MuiDrawer-paper": {
              width: railWidth(collapsed),
              transition: "width .2s ease",
              backgroundColor: "#ffffff",
              borderRight: "1px solid #eef2f7",
              /* The rail itself does not scroll: the list of links inside it
                 does, so the brand and whoever is signed in stay put. */
              overflowY: "hidden",
            },
          }}
          variant="permanent"
          anchor="left"
          open
        >
          <SidebarNav
            groups={groups}
            isRouteActive={isRouteActive}
            user={signedIn}
            footerLabel={`© ${currentYear} Easy Property`}
            collapsed={collapsed}
            onCollapse={onCollapse}
            onLogout={handleLogout}
          />
        </Drawer>
      )}

      {/* Mobile — the same body, not a second copy of it */}
      <Drawer
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            border: "none",
            backgroundColor: "#ffffff",
            overflowY: "hidden",
          },
        }}
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        <SidebarNav
          groups={groups}
          isRouteActive={isRouteActive}
          onNavigate={handleDrawerToggle}
          user={signedIn}
          footerLabel={`© ${currentYear} Easy Property`}
          onLogout={handleLogout}
        />
      </Drawer>
    </>
  );
}
